import { useEffect, useRef, useState } from 'react';

const HIGHLIGHT_DURATION = 100;
export function useHighlightedRows<T extends { id: string }>(
  rows: T[],
  highlightDuration = HIGHLIGHT_DURATION
) {
  const previous = useRef<T[]>([]);
  const [newRowIds, setNewRowIds] = useState<Set<string>>(new Set());
  const timeouts = useRef<Record<string, number>>({});

  useEffect(() => {
    // If there's no previous data, it's the first render—just set previous and skip highlight
    if (previous.current.length === 0) {
      previous.current = rows;
      return;
    }

    const prevIds = new Set(previous.current.map((r) => r.id));
    const currentIds = new Set(rows.map((r) => r.id));
    const newlyAddedIds = currentIds.difference(prevIds);

    // Highlight newly added rows and schedule removal
    newlyAddedIds.forEach((id) => {
      setNewRowIds((prev) => new Set(prev).add(id));
      // Clear any existing timer for this row
      if (timeouts.current[id]) {
        clearTimeout(timeouts.current[id]);
      }
      // Remove from highlight set after 500ms
      timeouts.current[id] = window.setTimeout(() => {
        setNewRowIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        delete timeouts.current[id];
      }, highlightDuration);
    });

    previous.current = rows;
  }, [highlightDuration, rows]);

  return newRowIds;
}
